import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { ArrowLeft } from 'lucide-react';
import React, { memo, useCallback } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { FaxTransmissionTable } from '@/components/fax/transmissions/FaxTransmissionTable';
import { FaxWizard } from '@/components/fax/wizard/FaxWizard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import useMatterSelection from '../../hooks/useMatterSelection';

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

interface FaxHomeProps {
  matterId: string;
  onSendFax: () => void;
}

const FaxHome: React.FC<FaxHomeProps> = memo(({ matterId, onSendFax }) => (
  <div className="flex flex-col h-full">
    {/* Header */}
    <div className="border-b border-zinc-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">Fax</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Send documents via fax for this case
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={onSendFax}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            aria-label="Send new fax"
          >
            <PaperAirplaneIcon className="h-4 w-4 mr-2" aria-hidden="true" />
            Send Fax
          </Button>
        </div>
      </div>
    </div>

    {/* Fax Transmissions */}
    <div className="flex-1 p-6 overflow-y-auto">
      <FaxTransmissionTable matterId={matterId} />
    </div>
  </div>
));

FaxHome.displayName = 'FaxHome';

/* -------------------------------------------------------------------------- */
/*                                 Breadcrumb                                 */
/* -------------------------------------------------------------------------- */

const Breadcrumb: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <Button variant="ghost" onClick={onBack} className="gap-2">
    <ArrowLeft className="h-4 w-4" /> Back to Fax
  </Button>
);

/* -------------------------------------------------------------------------- */
/*                            Fax Wizard Wrapper                             */
/* -------------------------------------------------------------------------- */

const FaxWizardPage: React.FC<{ matterId: string }> = memo(({ matterId }) => {
  const navigate = useNavigate();

  const handleSuccess = useCallback(() => {
    navigate('../');
  }, [navigate]);

  const handleCancel = useCallback(() => navigate('../'), [navigate]);

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb onBack={handleCancel} />
      <FaxWizard
        matterId={matterId}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </div>
  );
});

FaxWizardPage.displayName = 'FaxWizardPage';

/* -------------------------------------------------------------------------- */
/*                              Main Tab Export                               */
/* -------------------------------------------------------------------------- */

const MatterFaxTab: React.FC = () => {
  const { selectedMatterData, selectedMatterLoading } = useMatterSelection();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSendFax = useCallback(() => {
    navigate('fax-wizard');
  }, [navigate]);

  if (selectedMatterLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="border-b border-zinc-200 p-6">
          <Skeleton className="h-8 w-24 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!selectedMatterData?.id) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-500">No matter selected</p>
          <p className="text-sm text-gray-400 mt-2">
            Please select a matter to view fax transmissions
          </p>
        </div>
      </div>
    );
  }

  const matterId = selectedMatterData.id;

  return (
    <Routes location={location} key={location.pathname}>
      <Route
        path="/"
        element={<FaxHome matterId={matterId} onSendFax={handleSendFax} />}
      />
      <Route
        path="fax-wizard"
        element={<FaxWizardPage matterId={matterId} />}
      />
    </Routes>
  );
};

export default MatterFaxTab;
